// ===== 3. Soft Approval Status Card Component =====
// src/components/SoftApprovalStatusCard.tsx

import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  LinearProgress,
  Alert,
  CircularProgress,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Error,
  Refresh,
  Security,
  Assessment
} from '@mui/icons-material';
import { SoftApprovalAnalysis } from '../../../../services/ai/aiSoftApprovalService';

interface ScoreItemProps {
    label: string;
    score: number;
  }
  
  const ScoreItem: React.FC<ScoreItemProps> = ({ label, score }) => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2" fontWeight="bold">
          {Math.round(score * 100)}%
        </Typography>
      </Box>
      <LinearProgress 
        variant="determinate" 
        value={score * 100} 
        sx={{ 
          height: 6, 
          borderRadius: 3,
          backgroundColor: 'rgba(0,0,0,0.1)',
          '& .MuiLinearProgress-bar': {
            backgroundColor: score >= 0.8 ? '#4caf50' : score >= 0.6 ? '#ff9800' : '#f44336'
          }
        }} 
      />
    </Box>
  );

interface SoftApprovalStatusCardProps {
  analysis: SoftApprovalAnalysis | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}

const SoftApprovalStatusCard: React.FC<SoftApprovalStatusCardProps> = ({
  analysis,
  loading,
  error,
  onRetry
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return {
          color: theme.palette.success.main,
          backgroundColor: '#f0fff4',
          icon: <CheckCircle sx={{ color: theme.palette.success.main }} />,
          title: 'Application Approved',
          subtitle: 'Ready for payment and processing'
        };
      case 'CONDITIONAL':
        return {
          color: theme.palette.warning.main,
          backgroundColor: '#fffbf0',
          icon: <Warning sx={{ color: theme.palette.warning.main }} />,
          title: 'Conditional Approval',
          subtitle: 'Address issues below to proceed'
        };
      case 'REQUIRES_REVIEW':
        return {
          color: theme.palette.error.main,
          backgroundColor: '#fff5f5',
          icon: <Error sx={{ color: theme.palette.error.main }} />,
          title: 'Requires Review',
          subtitle: 'Critical issues must be resolved'
        };
      default:
        return {
          color: theme.palette.grey[500],
          backgroundColor: '#f5f5f5',
          icon: <Assessment sx={{ color: theme.palette.grey[500] }} />,
          title: 'Analysis Pending',
          subtitle: 'Waiting for analysis results'
        };
    }
  };

  if (loading) {
    return (
      <Card sx={{ mb: 3, border: `2px solid ${theme.palette.primary.light}` }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="center" py={4}>
            <Box textAlign="center">
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                <Security sx={{ mr: 1, verticalAlign: 'middle' }} />
                AI is reviewing the application...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Analyzing completeness, consistency, compliance, and documentation
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ mb: 3, border: `2px solid ${theme.palette.error.light}` }}>
        <CardContent>
          <Alert 
            severity="error" 
            action={
              <Button color="inherit" size="small" onClick={onRetry} startIcon={<Refresh />}>
                Retry
              </Button>
            }
          >
            <Typography variant="h6" gutterBottom>
              Soft Approval Analysis Failed
            </Typography>
            <Typography variant="body2">
              {error}. You can still proceed with manual review or retry the analysis.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!analysis) {
    return null;
  }

  const statusConfig = getStatusConfig(analysis.approvalStatus);

  return (
    <Card sx={{ 
      mb: 3, 
      border: `2px solid ${statusConfig.color}`,
      backgroundColor: statusConfig.backgroundColor
    }}>
      <CardContent>
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            {statusConfig.icon}
            <Box>
              <Typography variant="h6" sx={{ color: statusConfig.color, fontWeight: 600 }}>
                {statusConfig.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {statusConfig.subtitle}
              </Typography>
            </Box>
          </Box>
          
          <Box display="flex" alignItems="center" gap={1}>
            <Chip 
              label={`${Math.round(analysis.overallConfidence * 100)}% Confidence`}
              size="small"
              sx={{ backgroundColor: 'rgba(255,255,255,0.7)' }}
            />
            <Button size="small" onClick={onRetry} startIcon={<Refresh />}>
              Re-analyze
            </Button>
          </Box>
        </Box>

        {/* Scores */}
        <Box mb={3}>
          <Typography variant="subtitle2" gutterBottom>
            Analysis Breakdown
          </Typography>
          <Box display="grid" gridTemplateColumns={isMobile ? '1fr' : 'repeat(2, 1fr)'} gap={2}>
            <ScoreItem label="Completeness" score={analysis.completenessScore} />
            <ScoreItem label="Consistency" score={analysis.consistencyScore} />
            <ScoreItem label="Compliance" score={analysis.complianceScore} />
            <ScoreItem label="Documentation" score={analysis.documentationScore} />
          </Box>
        </Box>

        {/* Summary */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Summary
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={2}>
            <Chip 
              label={`${analysis.summary.totalIssues} Issues Found`}
              size="small"
              color={analysis.summary.totalIssues === 0 ? 'success' : 'warning'}
            />
            <Chip 
              label={analysis.summary.readyForPayment ? 'Ready for Payment' : 'Not Ready for Payment'}
              size="small"
              color={analysis.summary.readyForPayment ? 'success' : 'error'}
            />
            <Chip 
              label={`Est. Processing: ${analysis.summary.estimatedProcessingTime}`}
              size="small"
              variant="outlined"
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SoftApprovalStatusCard;

//---------------------------------------------------------------------------------------------------------------------------------------------------